import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import { FilePdf, X } from 'react-bootstrap-icons';
import { apiRequest } from '../utils/api';

const defaultState = {
  fileNameId: '',
  outletId: '',
  notes: '',
};

const UploadFileForm = ({ show, onHide, fileNames = [], outlets = [], onUploaded }) => {
  const [formState, setFormState] = useState(defaultState);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show) {
      setFormState(defaultState);
      setSelectedFile(null);
      setError('');
      setIsSubmitting(false);
    }
  }, [show]);

  const sortedFileNames = useMemo(
    () => [...fileNames].sort((a, b) => a.fileName.localeCompare(b.fileName)),
    [fileNames],
  );

  const sortedOutlets = useMemo(
    () => [...outlets].sort((a, b) => a.branchName.localeCompare(b.branchName)),
    [outlets],
  );

  const handleFileSelect = (event) => {
    const [file] = event.target.files ?? [];
    setSelectedFile(file || null);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formState.fileNameId) {
      setError('Please select a file name.');
      return;
    }

    if (!formState.outletId) {
      setError('Please select an outlet.');
      return;
    }

    if (!selectedFile) {
      setError('Please choose a file to upload.');
      return;
    }

    try {
      setError('');
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append('fileNameId', formState.fileNameId);
      formData.append('outletId', formState.outletId);
      formData.append('notes', formState.notes);
      formData.append('file', selectedFile);

      const { data } = await apiRequest('/api/admin/files/documents', {
        method: 'POST',
        body: formData,
      });

      if (typeof onUploaded === 'function') {
        onUploaded(data);
      }

      onHide();
    } catch (uploadError) {
      setError(uploadError.message || 'Failed to upload file.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Upload File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="py-2">
              {error}
            </Alert>
          )}
          <Form.Group className="mb-3" controlId="fileName">
            <Form.Label>
              File Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="fileNameId"
              value={formState.fileNameId}
              onChange={handleChange}
            >
              <option value="">Select File Name</option>
              {sortedFileNames.map((file) => (
                <option key={file.id} value={file.id}>
                  {file.fileName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="outlet">
            <Form.Label>
              Outlet <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="outletId"
              value={formState.outletId}
              onChange={handleChange}
            >
              <option value="">Select Outlet</option>
              {sortedOutlets.map((outlet) => (
                <option key={outlet.id} value={outlet.id}>
                  {outlet.branchName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="file">
            <Form.Label>
              File <span className="text-danger">*</span>
            </Form.Label>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose File
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => fileInputRef.current?.click()}
              >
                Browse
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="d-none"
              onChange={handleFileSelect}
            />
            {selectedFile && (
              <div className="mt-2 p-2 bg-light rounded d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <FilePdf className="text-danger me-2" />
                  <span className="small">{selectedFile.name}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                >
                  <X />
                </button>
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-0" controlId="notes">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={formState.notes}
              onChange={handleChange}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-danger" disabled={isSubmitting}>
            {isSubmitting ? 'Uploading…' : 'Upload'}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UploadFileForm;